import React, { useRef, useContext } from 'react';
import { string, number, object } from 'prop-types';
import { TextStyled } from '@nike/nike-design-system-components';
import { CaretRight } from '@nike/nike-design-system-icons';
import AnalyticsEventTypes from '../../utils/analyticsEvent';
import { LOG_OUT_BUTTON_PATH } from '../../constants';
import Link from '../Link';
import { MENU_OPTION_CLASS, PANEL_TEXT_CLASS } from '../MobileMenu/styles';
import { FocusContext } from '../MobileMenu';

const getSubMenuChildren = subMenu => {
  if (subMenu?.type === 'header' || subMenu?.type === 'link') return subMenu?.children;
  if (subMenu.children.length > 0) {
    return getSubMenuChildren(subMenu?.children[0]);
  }
};

/**
 * this was created so a linkRef could be created per link and passed into state
 * so we could return focus to the sub menu that this link might spawn
 * @param {object} props
 * @param {object | null} props.subMenuItem data required for link
 * @param {number} props.index
 * @param {string} props.linkLabel
 * @param {string} props.menuTitle
 * @returns {React.ReactElement}
 */
const PanelLink = ({ subMenuItem, index, linkLabel, menuTitle }) => {
  const { openSubMenu } = useContext(FocusContext);
  const linkRef = useRef(null);
  const hasChildren = Boolean(subMenuItem.children?.length);

  // in order to match analytic events in V2 we need to change the join character inside the account menu
  const joinCharacter =
    subMenuItem.analyticsEventName === AnalyticsEventTypes.CLICK_NAV_ACCOUNT ? ':' : '>';
  const eventPayload = {
    props: { path: subMenuItem?.path?.join(joinCharacter) || '', hashKey: subMenuItem?.hashKey },
    type: subMenuItem.analyticsEventName || AnalyticsEventTypes.SHOPPING_EVENT,
  };
  // Certain analytic types require the url as part of the payload.
  if (subMenuItem.url) {
    eventPayload.props.href = subMenuItem.url;
  }
  // opens new menu if link has associated children or signs user out if it's the log out button
  const getOnClickHandler = () => {
    if (hasChildren)
      return () =>
        openSubMenu(
          getSubMenuChildren(subMenuItem),
          menuTitle,
          linkLabel,
          linkRef,
          subMenuItem.url,
        );
    if (subMenuItem.path?.join(':') === LOG_OUT_BUTTON_PATH)
      return () => window.webShellClient.identity.signOut().finally({});
    return null;
  };

  const keyDownHandler = ({ nativeEvent }) => {
    switch (nativeEvent.which) {
      case 32: // SPACE
        // sorry for this syntax, but we need to call the function returned from getOnClickHandler
        getOnClickHandler()();
        break;

      default:
        break;
    }
  };
  return (
    <Link
      className={MENU_OPTION_CLASS}
      clickEventPayload={eventPayload}
      dataTestId={`panel-link__${linkLabel ? linkLabel?.replace(/ /g, '_') : `panelItem-${index}`}`}
      href={!hasChildren ? subMenuItem.url : null}
      onClick={getOnClickHandler()}
      onKeyDown={keyDownHandler}
      ref={linkRef}
      shouldSendEvent={!hasChildren}
    >
      <TextStyled appearance="body1Strong" className={PANEL_TEXT_CLASS} color="secondary">
        {linkLabel}
      </TextStyled>
      {hasChildren && <CaretRight />}
    </Link>
  );
};

PanelLink.propTypes = {
  index: number,
  linkLabel: string,
  menuTitle: string,
  subMenuItem: object,
};

export default PanelLink;
